// Screen→world entity picking (bee-inspect-panel; `client-rendering` "Screen→world entity
// picking", UI.md §2/§8). Pure and Pixi-free like its `draw-list.ts`/`facing.ts`/
// `ground-scatter.ts` neighbours: no store, no renderer, no state — just (screen point,
// camera, candidates) → id or null.
//
// Two things it must get right, both spec'd:
//
//   1. The ×0.72 y-squash. UI.md §8 says "picking inverts it", and the only honest way to
//      guarantee the forward and inverse transforms never drift is to go through
//      `projection.ts`'s own `screenToWorld` — which is what derives the world-space hit
//      ellipse below, rather than a hand-rolled `1/(scale*Y_SQUASH)`.
//   2. A ≥44px hit target (UI.md §2). A bee sprite is 24px at unit zoom and a couple of
//      pixels when zoomed out to survey the field, so a sprite-bounds test would be
//      untappable on a phone. The target is therefore a fixed radius in SCREEN space,
//      independent of camera scale — which is why the world-space region it maps back to is
//      an ellipse (taller in world y by exactly 1/Y_SQUASH), not a circle.
//
// Tie-breaking mirrors the sim's own proximity queries (DESIGN §7): nearest first, then
// lowest entity id — so a tap between two overlapping bees always resolves the same way, and
// never depends on the order the candidates happen to be iterated in.

import { screenToWorld, type Camera, type ScreenPoint } from './projection';

/** The picker's hit radius in SCREEN pixels. 22 px ⇒ a 44 px effective target diameter,
 * the design system's normative minimum (UI.md §2 `--hit-target`). Deliberately not a
 * balance value and not zoom-dependent: it is a touch-ergonomics constant. */
export const PICK_RADIUS_PX = 22;

/**
 * How far ABOVE a bee's ground origin (screen px) the hit target is centred.
 *
 * A bee's world position is its **ground contact point**, which is where its shadow is drawn; the
 * 24 px sprite is drawn above it (bottom-anchored, plus a hover gap when flying). Centring the hit
 * target on the world position therefore centres it on the *shadow*, and a tap that visibly lands
 * on the bee could miss while one on empty ground below it could hit (2026-08-11 finding).
 *
 * Half the sprite height puts the target on the body for both gaits: the flying hover gap is a few
 * px and {@link PICK_RADIUS_PX}'s 22 px radius comfortably absorbs that difference, so this stays
 * one constant rather than a gait-dependent offset the picker would have to know about.
 *
 * Screen-space, like the radius — it must not scale with the camera, or the target would drift off
 * the sprite at every zoom but one.
 */
export const PICK_SPRITE_RISE_PX = 12;

/** The minimum a candidate needs to be pickable: a stable id and a world position. A wire
 * `BeeRecord` satisfies it structurally, so no adapter/copy is needed at the call site. */
export interface Pickable {
  readonly id: number;
  readonly position: readonly [number, number];
}

/**
 * The id of the entity nearest `screen` within the {@link PICK_RADIUS_PX} hit target, or
 * `null` if nothing is inside it.
 *
 * The hit test is done in *normalized* space: the screen-space circle of radius `radiusPx`
 * is inverted (through `screenToWorld`, so the y-squash is inverted exactly once, in
 * `projection.ts`) into its world-space half-extents, and each candidate's world offset is
 * divided by those. The normalized distance is therefore the candidate's screen-space
 * distance divided by `radiusPx` — so ranking by it ranks by screen distance, which is what
 * a finger actually cares about.
 *
 * Total: a degenerate camera (zero/negative/non-finite scale) yields non-finite half-extents
 * and returns `null` rather than propagating NaN into the comparison.
 */
export function pickEntityAt(
  screen: ScreenPoint,
  camera: Camera,
  candidates: Iterable<Pickable>,
  radiusPx: number = PICK_RADIUS_PX,
): number | null {
  // The probe point is pushed DOWN in screen space before inversion, which is equivalent to
  // lifting the hit target UP onto the sprite: a tap on the body then maps to a world point near
  // the bee's ground origin, which is what the candidates carry. See PICK_SPRITE_RISE_PX.
  const probe: ScreenPoint = { x: screen.x, y: screen.y + PICK_SPRITE_RISE_PX };
  const [centreX, centreY] = screenToWorld(camera, probe);
  const [edgeX, edgeY] = screenToWorld(camera, { x: probe.x + radiusPx, y: probe.y + radiusPx });
  // The world-space half-extents of the screen-space hit circle: `radiusPx / scale` on x and
  // `radiusPx / (scale * Y_SQUASH)` on y — read off the inverse transform rather than
  // restated, so a future projection change cannot silently desync picking from rendering.
  const halfX = edgeX - centreX;
  const halfY = edgeY - centreY;
  if (!Number.isFinite(halfX) || !Number.isFinite(halfY) || halfX <= 0 || halfY <= 0) {
    return null;
  }

  let bestId: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const nx = (candidate.position[0] - centreX) / halfX;
    const ny = (candidate.position[1] - centreY) / halfY;
    const distance = Math.hypot(nx, ny);
    if (distance > 1) {
      continue; // outside the hit target
    }
    // Nearest first, then lowest id (DESIGN §7) — order-independent by construction.
    if (distance < bestDistance || (distance === bestDistance && bestId !== null && candidate.id < bestId)) {
      bestDistance = distance;
      bestId = candidate.id;
    }
  }
  return bestId;
}
